/**
 * Support Contracts (UST-3159) — the customer support contracts the team used
 * to keep in Excel, and the hours logged against each.
 *
 * Access is granted feature by feature, never as one switch. Every feature a
 * user can be given lives in SUPPORT_CONTRACT_PERMISSIONS below: the API checks
 * these keys, and the Settings screen draws its rows from the same list, so a
 * feature cannot exist in one place and be missing from the other.
 */
export const SUPPORT_CONTRACT_PERMISSION_LEVELS = {
    view: ['none', 'view'],
    edit: ['none', 'view', 'edit'],
    action: ['none', 'allow'],
};
export const SUPPORT_CONTRACT_LEVEL_LABELS = {
    none: 'Hidden',
    view: 'Read only',
    edit: 'Edit',
    allow: 'Allowed',
};
export const SUPPORT_CONTRACT_PERMISSIONS = [
    // ─── Contracts ────────────────────────────────────────────────────────────
    {
        key: 'contracts.list',
        group: 'Contracts',
        kind: 'view',
        label: 'See contracts',
        description: 'Open the Support Contracts tab and see the contract list. Everything below needs this.',
    },
    {
        key: 'contracts.create',
        group: 'Contracts',
        kind: 'action',
        label: 'Create contracts',
        description: 'Add a new contract.',
    },
    {
        key: 'contracts.delete',
        group: 'Contracts',
        kind: 'action',
        label: 'Delete contracts',
        description: 'Delete a contract together with its time logs.',
    },
    {
        key: 'contracts.export',
        group: 'Contracts',
        kind: 'action',
        label: 'Export to Excel',
        description: 'Download the contract list as an Excel file.',
    },
    {
        key: 'contracts.import',
        group: 'Contracts',
        kind: 'action',
        label: 'Import from Excel',
        description: 'Bring contracts and time sheets in from the team’s Excel files.',
    },
    // ─── Contract details ─────────────────────────────────────────────────────
    {
        key: 'details.customer',
        group: 'Contract details',
        kind: 'edit',
        label: 'Customer & project',
        description: 'Project name and other name, and changing the customer name. The customer name itself is always shown — it is how each contract is told apart.',
    },
    {
        key: 'details.classification',
        group: 'Contract details',
        kind: 'edit',
        label: 'Region & contract type',
        description: 'Which region the contract belongs to and what kind of contract it is.',
    },
    {
        key: 'details.dates',
        group: 'Contract details',
        kind: 'edit',
        label: 'Dates',
        description: 'Start date, end date and go-live date.',
    },
    {
        key: 'details.hours',
        group: 'Contract details',
        kind: 'edit',
        label: 'Contract hours',
        description: 'Total hours the contract includes, and the hours used so far.',
    },
    {
        key: 'details.state',
        group: 'Contract details',
        kind: 'edit',
        label: 'Status',
        description: 'Put a contract on hold, cancel it, or make it active again.',
    },
    {
        key: 'details.services',
        group: 'Contract details',
        kind: 'edit',
        label: 'Included services & weights',
        description: 'Which service types the contract covers, and any weight changed for this contract only.',
    },
    {
        key: 'details.notes',
        group: 'Contract details',
        kind: 'edit',
        label: 'Notes',
        description: 'Free-text notes on the contract.',
    },
    // ─── People ───────────────────────────────────────────────────────────────
    {
        key: 'people.accountManager',
        group: 'People',
        kind: 'edit',
        label: 'Account manager',
        description: 'Account manager — the user, and the name, phone and email given to customers whose contract expired or ran out of hours.',
    },
    {
        key: 'people.projectManager',
        group: 'People',
        kind: 'edit',
        label: 'Project manager',
        description: 'Project manager name, phone and email.',
    },
    {
        key: 'people.contacts',
        group: 'People',
        kind: 'edit',
        label: 'Authorized contacts',
        description: 'The customer’s authorized contacts. The support agent recognises a customer by these.',
    },
    // ─── Time logs ────────────────────────────────────────────────────────────
    {
        key: 'timeLogs.view',
        group: 'Time logs',
        kind: 'view',
        label: 'See time logs',
        description: 'Open a contract’s Time Logs window.',
    },
    {
        key: 'timeLogs.add',
        group: 'Time logs',
        kind: 'action',
        label: 'Add time logs',
        description: 'Add a line to a contract’s time logs.',
    },
    {
        key: 'timeLogs.edit',
        group: 'Time logs',
        kind: 'action',
        label: 'Edit time logs',
        description: 'Change a line already logged.',
    },
    {
        key: 'timeLogs.delete',
        group: 'Time logs',
        kind: 'action',
        label: 'Delete time logs',
        description: 'Remove a logged line.',
    },
    {
        key: 'timeLogs.fromTicket',
        group: 'Time logs',
        kind: 'action',
        label: 'Log time from a ticket',
        description: 'Log hours from inside a support ticket.',
    },
    // ─── Ticket usage (ticket-based contracts) ───────────────────────────────
    {
        key: 'ticketUses.view',
        group: 'Ticket usage',
        kind: 'view',
        label: 'See ticket usage',
        description: 'Open a contract’s Ticket usage window: the tickets it has used.',
    },
    {
        key: 'ticketUses.add',
        group: 'Ticket usage',
        kind: 'action',
        label: 'Count tickets',
        description: 'Count a ticket against a contract by hand, or mark a ticket to count.',
    },
    {
        key: 'ticketUses.delete',
        group: 'Ticket usage',
        kind: 'action',
        label: 'Remove counted tickets',
        description: 'Remove a line from a contract’s ticket usage.',
    },
    {
        key: 'tickets.linkContract',
        group: 'Time logs',
        kind: 'action',
        label: 'Set a ticket\'s contract ID',
        description: 'Enter the contract ID on a support ticket, or mark it as having no contract ID, and pick its service type.',
    },
    // ─── Service types ────────────────────────────────────────────────────────
    {
        key: 'serviceTypes.details',
        group: 'Service types',
        kind: 'edit',
        label: 'Service types',
        description: 'The list of service types — their names and descriptions.',
    },
    {
        key: 'serviceTypes.weights',
        group: 'Service types',
        kind: 'edit',
        label: 'Service type weights',
        description: 'How many contract hours one hour of each service type uses.',
    },
    {
        key: 'serviceTypes.create',
        group: 'Service types',
        kind: 'action',
        label: 'Add service types',
        description: 'Add a new service type.',
    },
    {
        key: 'serviceTypes.delete',
        group: 'Service types',
        kind: 'action',
        label: 'Delete service types',
        description: 'Remove a service type.',
    },
    // ─── Contract types ───────────────────────────────────────────────────────
    {
        key: 'contractTypes.details',
        group: 'Contract types',
        kind: 'edit',
        label: 'Contract types',
        description: 'The list of contract types (Contract Support, Free Support …) — names and descriptions.',
    },
    {
        key: 'contractTypes.create',
        group: 'Contract types',
        kind: 'action',
        label: 'Add contract types',
        description: 'Add a new contract type.',
    },
    {
        key: 'contractTypes.delete',
        group: 'Contract types',
        kind: 'action',
        label: 'Delete contract types',
        description: 'Remove a contract type.',
    },
    // ─── Settings ─────────────────────────────────────────────────────────────
    {
        key: 'settings.defaultContact',
        group: 'Settings',
        kind: 'edit',
        label: 'No-contract contact',
        description: 'Who a customer with no contract is told to contact.',
    },
    {
        key: 'settings.rounding',
        group: 'Settings',
        kind: 'edit',
        label: 'Round hours up',
        description: 'Whether logged hours are rounded up to a full hour before they count against a contract (policy: any fraction of an hour is a full hour).',
    },
];
/** The highest level each kind allows. */
const TOP_LEVEL = {
    view: 'view',
    edit: 'edit',
    action: 'allow',
};
/** What an admin has: every feature at its highest level. */
export function fullSupportContractPermissions() {
    return Object.fromEntries(SUPPORT_CONTRACT_PERMISSIONS.map((p) => [p.key, TOP_LEVEL[p.kind]]));
}
/** Read only on everything that can be seen; no actions. */
export function readOnlySupportContractPermissions() {
    return Object.fromEntries(SUPPORT_CONTRACT_PERMISSIONS.map((p) => [p.key, p.kind === 'action' ? 'none' : 'view']));
}
/** Nothing at all. */
export function emptySupportContractPermissions() {
    return Object.fromEntries(SUPPORT_CONTRACT_PERMISSIONS.map((p) => [p.key, 'none']));
}
/**
 * Managed by admins only, in Settings → Support settings: contract types,
 * service types, contract settings, import and export. Anyone else can at
 * most see them (forms still list the types) and never change or run them.
 */
export const SUPPORT_CONTRACT_ADMIN_ONLY_KEYS = new Set([
    'contracts.export',
    'contracts.import',
    'serviceTypes.details',
    'serviceTypes.weights',
    'serviceTypes.create',
    'serviceTypes.delete',
    'contractTypes.details',
    'contractTypes.create',
    'contractTypes.delete',
    'settings.defaultContact',
    'settings.rounding',
]);
/** The levels a user grant may take for a feature — admin-only ones stop at "Read only". */
export function supportContractGrantLevels(p) {
    const levels = SUPPORT_CONTRACT_PERMISSION_LEVELS[p.kind];
    return SUPPORT_CONTRACT_ADMIN_ONLY_KEYS.has(p.key)
        ? levels.filter((l) => l === 'none' || l === 'view')
        : levels;
}
/**
 * Turn whatever is stored into a complete, valid map. Unknown keys are
 * dropped, missing keys and levels a kind cannot take become 'none', and when
 * "See contracts" is off nothing else counts — it is the master switch.
 * Admin-only features are capped at read only (admins never go through here).
 */
export function normalizeSupportContractPermissions(raw) {
    const stored = raw && typeof raw === 'object' ? raw : {};
    const out = emptySupportContractPermissions();
    for (const p of SUPPORT_CONTRACT_PERMISSIONS) {
        const level = stored[p.key];
        if (typeof level === 'string' &&
            supportContractGrantLevels(p).includes(level)) {
            out[p.key] = level;
        }
        else if (level === 'edit' && SUPPORT_CONTRACT_ADMIN_ONLY_KEYS.has(p.key)) {
            // Granted before these became admin-only: they keep seeing it.
            out[p.key] = 'view';
        }
    }
    if (out['contracts.list'] === 'none')
        return emptySupportContractPermissions();
    return out;
}
/** True when the level lets the user see the feature. */
export function canViewContractFeature(level) {
    return level !== 'none';
}
/** True when the level lets the user change the feature or perform the action. */
export function canChangeContractFeature(level) {
    return level === 'edit' || level === 'allow';
}
/**
 * Which finished tickets count against a ticket-based contract:
 *   always       — every ticket on the contract, once it is finished
 *   user_decides — only the ones marked "count this ticket"
 */
export const SUPPORT_TICKET_COUNT_MODES = ['always', 'user_decides'];
// ─── Contracts ───────────────────────────────────────────────────────────────
/** What a person sets by hand. */
export const SUPPORT_CONTRACT_STATES = ['active', 'on_hold', 'cancelled'];
export const SUPPORT_CONTRACT_STATE_LABELS = {
    active: 'Active',
    on_hold: 'On hold',
    cancelled: 'Cancelled',
};
/**
 * What a contract actually is today — worked out from the state, the dates
 * and the hours used, never stored. This is what the agent acts on.
 */
export const SUPPORT_CONTRACT_STATUSES = [
    'active',
    'not_started',
    'hours_used_up',
    'tickets_used_up',
    'expired',
    'on_hold',
    'cancelled',
];
export const SUPPORT_CONTRACT_STATUS_LABELS = {
    active: 'Active',
    not_started: 'Not started',
    hours_used_up: 'Hours used up',
    tickets_used_up: 'Tickets used up',
    expired: 'Expired',
    on_hold: 'On hold',
    cancelled: 'Cancelled',
};
/** Regions offered in the form. Others can still be typed. */
export const SUPPORT_CONTRACT_REGIONS = ['KSA', 'EG'];
/** The most authorized contacts a contract holds. The form starts with one and adds more with "+". */
export const SUPPORT_CONTRACT_MAX_CONTACTS = 20;
/**
 * The status of a contract on `today` (YYYY-MM-DD). Dates are whole days: a
 * contract ending today is still valid all of today. A contract with no
 * total hours is never "hours used up".
 */
export function computeSupportContractStatus(c, today) {
    if (c.state === 'cancelled')
        return 'cancelled';
    if (c.state === 'on_hold')
        return 'on_hold';
    if (c.endDate && c.endDate < today)
        return 'expired';
    if (c.startDate && c.startDate > today)
        return 'not_started';
    if (c.totalHours !== null && c.usedHours >= c.totalHours)
        return 'hours_used_up';
    if (c.totalTickets != null && (c.usedTickets ?? 0) >= c.totalTickets)
        return 'tickets_used_up';
    return 'active';
}
/**
 * Ticket packages (policy §4.1.2): the period a contract's tickets are given
 * for. "30 tickets a month on a one-year contract" is `month` — unused
 * tickets do not move to the next month. `contract` (or null) is the whole
 * contract, as before.
 */
export const SUPPORT_TICKET_PERIODS = ['contract', 'month', 'quarter', 'year'];
export const SUPPORT_TICKET_PERIOD_LABELS = {
    contract: 'Whole contract',
    month: 'Each month',
    quarter: 'Each quarter',
    year: 'Each year',
};
const TICKET_PERIOD_MONTHS = {
    month: 1,
    quarter: 3,
    year: 12,
};
/** `date` (YYYY-MM-DD) plus `months`, the day kept or moved to the month's last day. */
function addMonths(date, months) {
    const [y, m, d] = date.split('-').map(Number);
    const index = y * 12 + (m - 1) + months;
    const year = Math.floor(index / 12);
    const month = index % 12;
    const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const day = Math.min(d, last);
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * The ticket period `today` falls in: from (inclusive) and to (exclusive),
 * YYYY-MM-DD. Periods run from the contract's start date (a contract starting
 * on the 18th has months 18th → 17th); without a start date they are calendar
 * months, quarters or years. Null: the whole contract is one period.
 */
export function supportTicketPeriodRange(period, startDate, today) {
    if (!period || period === 'contract' || !(period in TICKET_PERIOD_MONTHS))
        return null;
    const len = TICKET_PERIOD_MONTHS[period];
    if (!startDate) {
        const [y, m] = today.split('-').map(Number);
        const first = Math.floor((m - 1) / len) * len;
        const from = `${y}-${String(first + 1).padStart(2, '0')}-01`;
        return { from, to: addMonths(from, len) };
    }
    if (today < startDate)
        return { from: startDate, to: addMonths(startDate, len) };
    const [sy, sm] = startDate.split('-').map(Number);
    const [ty, tm] = today.split('-').map(Number);
    let k = Math.floor((ty * 12 + tm - (sy * 12 + sm)) / len);
    // Counted from the start each time (not step by step), so a start on the 31st keeps the 31st.
    while (k > 0 && addMonths(startDate, k * len) > today)
        k -= 1;
    while (addMonths(startDate, (k + 1) * len) <= today)
        k += 1;
    return { from: addMonths(startDate, k * len), to: addMonths(startDate, (k + 1) * len) };
}
/**
 * A null hour or ticket cap on an hours- or tickets-based type. The screens
 * and the export say "Unlimited". A type with no kind keeps today's "no limit".
 */
export function isUnlimitedContractCap(total, typeBased) {
    return typeBased === true && total == null;
}
/** What an unlimited cap reads as, in the list, the bars and the export. */
export const SUPPORT_CONTRACT_UNLIMITED_LABEL = 'Unlimited';
/** Whole days from `today` to `endDate` (negative once it has passed). */
export function supportContractDaysLeft(endDate, today) {
    if (!endDate)
        return null;
    const ms = Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
    return Math.round(ms / 86_400_000);
}
/**
 * Which permission covers each field a contract can be saved with. The API
 * refuses a save that touches a field the user cannot edit, and the form
 * greys out the same fields.
 */
export const SUPPORT_CONTRACT_FIELD_PERMISSION = {
    customerName: 'details.customer',
    projectName: 'details.customer',
    otherName: 'details.customer',
    region: 'details.classification',
    contractTypeId: 'details.classification',
    startDate: 'details.dates',
    endDate: 'details.dates',
    liveDate: 'details.dates',
    totalHours: 'details.hours',
    totalTickets: 'details.hours',
    ticketPeriod: 'details.hours',
    state: 'details.state',
    notes: 'details.notes',
    accountManager: 'people.accountManager',
    accountManagerUserId: 'people.accountManager',
    projectManager: 'people.projectManager',
    contacts: 'people.contacts',
    services: 'details.services',
};
/**
 * The hours a time log uses from its contract: rounded up to a full hour
 * first when `roundUp` is on (policy §17), then multiplied by the weight.
 * Two decimals, so 0.5 h at weight 1.5 is exactly 0.75.
 */
export function computeBilledHours(hours, weight, roundUp) {
    const counted = roundUp ? Math.ceil(hours) : hours;
    return Math.round(counted * weight * 100) / 100;
}
export const SUPPORT_CUSTOMER_CONTRACT_LABELS = {
    no_contract: 'No contract',
    active: 'Contract active',
    not_started: 'Contract not started',
    hours_used_up: 'Contract hours used up',
    tickets_used_up: 'Contract tickets used up',
    expired: 'Contract expired',
    on_hold: 'Contract on hold',
    cancelled: 'Contract cancelled',
};
/**
 * The policy's exceptions — the only reasons the support agent may open a
 * ticket for a customer without an active contract:
 *   system_down — the whole system is down on hosting we manage (policy
 *                 §4.4.4, e.g. Odoo.sh); charges may apply later
 *   free_admin  — adding users or changing permissions when the customer has
 *                 no system admin (§4.4.2): by email only
 *   license     — a system licence problem (§4.4.3)
 */
export const SUPPORT_CONTRACT_EXCEPTIONS = ['system_down', 'free_admin', 'license'];
export const SUPPORT_CONTRACT_EXCEPTION_LABELS = {
    system_down: 'System down (out of contract)',
    free_admin: 'Free admin / access request',
    license: 'System licence issue',
};
// ─── Contract ID and ticket number (UST-3159) ────────────────────────────────
/**
 * Letters and digits a contract ID is made of: no 0/O, 1/I/L, so a code read
 * out over the phone or copied by hand cannot be mistaken for another.
 */
export const SUPPORT_CONTRACT_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
/**
 * A contract ID as typed, written or read out ("sc 7k4p 9qx2", "7K4P9QX2",
 * "SC-7K4P-9QX2") in its stored form "SC-7K4P-9QX2", or null when it cannot
 * be one. Says nothing about whether the contract exists.
 */
export function normalizeSupportContractCode(raw) {
    let compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    // The "SC" prefix is optional; a code's own characters may start with SC too.
    if (compact.length === 10 && compact.startsWith('SC'))
        compact = compact.slice(2);
    if (compact.length !== 8)
        return null;
    for (const ch of compact)
        if (!SUPPORT_CONTRACT_CODE_ALPHABET.includes(ch))
            return null;
    return `SC-${compact.slice(0, 4)}-${compact.slice(4)}`;
}
/** A ticket number as people write it: "#26100087". */
export function formatSupportTicketNumber(number) {
    return `#${number}`;
}
/**
 * Settings → Support Contracts → Danger zone: deleting every contract at once
 * needs this phrase typed exactly, in the popup and again by the API.
 */
export const SUPPORT_CONTRACTS_DELETE_ALL_PHRASE = 'delete-support-contracts';
/** What the rules and the minimum apply to: a ticket's total time, or each time log line. */
export const SUPPORT_ROUNDING_SCOPES = ['ticket', 'entry'];
/** The "round up to a full hour" switch, as rules. */
export const SUPPORT_HOURLY_ROUNDING = {
    brackets: [],
    repeatMinutes: 60,
    graceMinutes: 0,
};
/** Minutes billed for  of work under the rules (no minimum). */
export function roundedMinutes(minutes, rules) {
    if (minutes <= 0)
        return 0;
    if (!rules)
        return minutes;
    const brackets = [...rules.brackets].sort((a, b) => a.upToMinutes - b.upToMinutes);
    for (const b of brackets)
        if (minutes <= b.upToMinutes)
            return b.billMinutes;
    const last = brackets[brackets.length - 1];
    const base = last?.billMinutes ?? 0;
    const extra = minutes - (last?.upToMinutes ?? 0);
    if (!rules.repeatMinutes || rules.repeatMinutes <= 0)
        return base + extra;
    const block = rules.repeatMinutes;
    const whole = Math.floor(extra / block);
    const part = extra - whole * block;
    return base + whole * block + (part > rules.graceMinutes ? block : 0);
}
/** Minutes billed for a ticket's (or a line's) time: the rules, then the minimum. */
export function billedMinutes(minutes, rules, minMinutes) {
    if (minutes <= 0)
        return 0;
    return Math.max(roundedMinutes(minutes, rules), minMinutes ?? 0);
}
/** Hours to minutes, the way time logs store hours (two decimals). */
export function hoursToMinutes(hours) {
    return Math.round(hours * 60);
}
/**
 * What one new line costs the contract, in contract hours.
 *   per entry — the line on its own (the minimum per line);
 *   per ticket — what the ticket's total costs with the line, minus without it,
 *                so the ticket always costs rules(total time) once.
 * ticketMinutesBefore is the time already logged on the ticket (per ticket
 * only; null for a line that is not on a ticket — it is billed per entry,
 * without the ticket minimum).
 */
export function billLineHours(hours, weight, policy, ticketMinutesBefore) {
    const minutes = hoursToMinutes(hours);
    let billed;
    if (ticketMinutesBefore === null) {
        billed = roundedMinutes(minutes, policy.rules);
    }
    else if (policy.scope === 'ticket') {
        billed =
            billedMinutes(ticketMinutesBefore + minutes, policy.rules, policy.minTicketMinutes) -
                billedMinutes(ticketMinutesBefore, policy.rules, policy.minTicketMinutes);
    }
    else {
        billed = billedMinutes(minutes, policy.rules, policy.minTicketMinutes);
    }
    return Math.round((billed / 60) * weight * 100) / 100;
}
/** "1 h 30 min", "45 min". */
export function formatSupportMinutes(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0)
        return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
//# sourceMappingURL=support-contracts.js.map